import { useEffect, useMemo, useState } from "react";
import { Background, ReactFlow, ReactFlowProvider } from "@xyflow/react";

import {
  Dialog,
  DialogTitle,
  DialogActions,
} from "V2.0/components/DialogFullWidth";
import { Button } from "V2.0/common/button";

import useLayout from "V2.0/HomePages/Workflows/Builder/useLayout";
import useAutoLayout from "../PipelineBuilderV2/Layout/useAutoLayout";
import PipelineBuilderProvider from "V2.0/HomePages/Pipelines/PipelineBuilder/PipelineBuilderProvider";

import { getInitialNodesAndEdges } from "../PipelineBuilderV2";
import {
  PipelineV2RunStatus,
  PipelineV2RunStatusBadgeRegistry,
  renderDefaultBadge,
} from "../PipelineRunsV2/PipelineV2RunStatusBadge";
import { nodeTypes } from "../PipelineBuilderV2/Nodes";
import { edgeTypes } from "../PipelineBuilderV2/Edges";
import NodeExecution from "./NodeExecution";
import { NodeExecutionStatus } from "./Components/NodeExecutionStatus";

export default PipelineV2RunPreview;

interface PipelineV2RunPreviewProps {
  pipelineRun: any;
  onClose: () => void;
  workspaceId: string;
}

function PipelineV2RunPreview(props: PipelineV2RunPreviewProps) {
  const { pipelineRun, onClose, workspaceId } = props;

  const {
    contact,
    status,
    currentNodeId,
    userIdentifier,
    nodeExecutions,
    pipelineSnapshot,
  } = pipelineRun;

  const { initialNodes: nodes, initialEdges: edges } =
    getInitialNodesAndEdges(pipelineSnapshot);

  const nodeExecutionsIndexedByNodeId = nodeExecutions.reduce((acc, item) => {
    const { nodeId } = item;
    return {
      ...acc,
      [nodeId]: item,
    };
  }, {});

  return (
    <ReactFlowProvider>
      <PipelineV2RunPreviewComponent
        nodes={nodes}
        edges={edges}
        status={status as PipelineV2RunStatus}
        contact={contact}
        onClose={onClose}
        userIdentifier={userIdentifier}
        workspaceId={workspaceId}
        currentNodeId={currentNodeId}
        nodeExecutionsRecord={nodeExecutionsIndexedByNodeId}
      />
    </ReactFlowProvider>
  );
}

interface PipelineV2RunPreviewComponentProps {
  nodes: Array<any>;
  edges: Array<any>;
  status: PipelineV2RunStatus;
  workspaceId: string;
  currentNodeId: string;
  userIdentifier: string;
  nodeExecutionsRecord: Record<string, any>;
  contact: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
  };
  onClose: () => void;
}

function PipelineV2RunPreviewComponent(
  props: PipelineV2RunPreviewComponentProps,
) {
  const {
    nodes,
    edges,
    status,
    contact,
    workspaceId,
    currentNodeId,
    userIdentifier,
    nodeExecutionsRecord,

    onClose,
  } = props;
  const [panel, setPanel] = useState({
    node: undefined,
    nodeExecution: undefined,
  });

  const layoutOptions = useMemo(
    () => ({ direction: "TB", spacing: [100, 240] }),
    [],
  );

  // useLayout();
  useAutoLayout(layoutOptions);

  const onViewNodeExecution = (node) => {
    const nodeExecution = nodeExecutionsRecord[node.id] ?? null;
    if (!!nodeExecution) {
      setPanel({ node, nodeExecution });
    }

    return;
  };

  const handleNodeClick = (e, node) => {
    onViewNodeExecution(node);
  };

  const getNodeExecution = (nodeId: string) => {
    const executedNode = nodeExecutionsRecord[nodeId] ?? null;
    const isExecuted = executedNode
      ? executedNode?.status !== NodeExecutionStatus.PENDING
      : false;

    return { isExecuted, executedNode };
  };

  const statusBadge =
    PipelineV2RunStatusBadgeRegistry[status] ?? renderDefaultBadge(status);

  return (
    <PipelineBuilderProvider
      value={{
        panel,
        setPanel,
        closePanel: () => {
          setPanel({
            node: undefined,
            nodeExecution: undefined,
          });
        },
        workspaceId,
        isPanelActive: !!panel.node,
        getNodeExecution,
        isPreviewMode: true,
        onConfigureNode: (node) => onViewNodeExecution(node),
      }}
    >
      <Dialog
        className="h-full"
        size="full"
        open={true}
        onClose={() => {}}
      >
        <div className="flex-1 flex flex-col relative">
          <DialogTitle className="shrink-0 sm:*:w-auto *:w-auto p-4 border-none shadow-sm">
            <div className="flex flex-col gap-y-2">
              <p>{userIdentifier}</p>
              <div>{statusBadge}</div>
            </div>
          </DialogTitle>
          <div className="flex-1 min-h-0 overflow-hidden relative">
            <ReactFlow
              fitView
              panOnScroll
              zoomOnScroll
              minZoom={0.5}
              nodeTypes={nodeTypes}
              edgeTypes={edgeTypes}
              defaultNodes={nodes}
              defaultEdges={edges}
              onNodeClick={handleNodeClick}
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              defaultEdgeOptions={{ deletable: false }}
            >
              <Background />
            </ReactFlow>
            {panel.node && <NodeExecution />}
          </div>
          <DialogActions className="shrink-0 sm:*:w-auto *:w-auto px-4 py-3 shadow-[0_-1px_2px_-1px_rgba(0,0,0,0.1)]">
            <Button
              outline
              onClick={onClose}
            >
              Close
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </PipelineBuilderProvider>
  );
}
